import { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { getSalesRoster, hideSalesRosterUser, unhideSalesRosterUser, updateSalesRosterMeta } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { Loader2, Users, ArrowUpDown, ArrowUp, ArrowDown, Calendar, AlertTriangle, Eye, EyeOff, Star, StickyNote } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';

const fmtEUR = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtEURcompact = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd/MM/yyyy'); } catch { return iso; }
};

// Status progetti — allineato a Gestione Progetti (label + colori badge)
// Gli stati `in_preparazione` e `terminato` NON vengono mostrati nella tabella riepilogo
const PROJECT_STATUS_META = {
  in_preparazione: { label: 'In Preparazione', color: 'bg-blue-100 text-blue-800 border-blue-200', hidden: true },
  partito:         { label: 'Partito',         color: 'bg-green-100 text-green-800 border-green-200' },
  congelato:       { label: 'Congelato',       color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  terminato:       { label: 'Terminato',       color: 'bg-gray-100 text-gray-800 border-gray-200', hidden: true },
};
const statusMeta = (s) => PROJECT_STATUS_META[(s || '').toLowerCase()] || { label: s || 'n/d', color: 'bg-slate-100 text-slate-700 border-slate-200' };

// Label italiane delle project_functions SALES
const SALES_FN_LABELS = {
  venditore: 'Venditore',
  setter: 'Setter',
  hunter: 'Hunter',
  hunter_senior: 'Hunter Sr.',
  sales_coach: 'Sales Coach',
  sales_coach_senior: 'Sales Coach Sr.',
  hunter_manager: 'Hunter Manager',
};

const expiryColor = (days, projectStatus) => {
  // Colori speciali basati sullo stato del progetto associato alla scadenza mostrata
  const st = (projectStatus || '').toLowerCase();
  if (st === 'congelato') return 'bg-blue-50 text-blue-700 border-blue-200 font-semibold';
  if (st === 'terminato') return 'bg-red-50 text-red-700 border-red-200 font-semibold';
  // Fasce standard per progetti attivi
  if (days === null || days === undefined) return null;
  if (days < 60)  return 'bg-red-50 text-red-700 border-red-200 font-semibold';
  if (days < 120) return 'bg-amber-50 text-amber-700 border-amber-200 font-semibold';
  return 'bg-green-50 text-green-700 border-green-200 font-medium';
};

const npsColor = (nps) => {
  if (nps === null || nps === undefined) return 'text-gray-400';
  if (nps >= 8) return 'text-green-700 font-semibold';
  if (nps >= 6) return 'text-amber-700 font-semibold';
  return 'text-red-700 font-semibold';
};

export default function SalesRosterPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [showHidden, setShowHidden] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [starringId, setStarringId] = useState(null);
  const [noteRow, setNoteRow] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [sortKey, setSortKey] = useState('total_sales');
  const [sortDir, setSortDir] = useState('desc'); // desc | asc
  const [detailRow, setDetailRow] = useState(null);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const res = await getSalesRoster({ include_hidden: (opts.showHidden ?? showHidden) ? 'true' : 'false' });
      setRows(res.data.data || []);
      setHiddenCount(res.data.hidden_count || 0);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Caricamento fallito', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast, showHidden]);

  useEffect(() => { load(); }, [load]);

  const toggleHidden = useCallback(async (row, e) => {
    e?.stopPropagation?.();
    const willHide = !row.is_hidden;
    setTogglingId(row.user_id);
    // Optimistic update — nessun reload/scroll-to-top
    setRows(prev => {
      if (willHide && !showHidden) {
        return prev.filter(r => r.user_id !== row.user_id);
      }
      return prev.map(r => r.user_id === row.user_id ? { ...r, is_hidden: willHide } : r);
    });
    setHiddenCount(c => c + (willHide ? 1 : -1));
    try {
      if (willHide) {
        await hideSalesRosterUser(row.user_id);
        toast({ title: 'Utente nascosto', description: `${row.name} non compare più nella rosa` });
      } else {
        await unhideSalesRosterUser(row.user_id);
        toast({ title: 'Utente ripristinato', description: `${row.name} torna visibile nella rosa` });
      }
    } catch (err) {
      // Rollback in caso di errore
      setRows(prev => {
        if (willHide && !showHidden) {
          return [...prev, { ...row, is_hidden: false }];
        }
        return prev.map(r => r.user_id === row.user_id ? { ...r, is_hidden: !willHide } : r);
      });
      setHiddenCount(c => c - (willHide ? 1 : -1));
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally { setTogglingId(null); }
  }, [showHidden, toast]);

  const toggleStar = useCallback(async (row, e) => {
    e?.stopPropagation?.();
    const next = !row.is_starred;
    setStarringId(row.user_id);
    // Optimistic
    setRows(prev => prev.map(r => r.user_id === row.user_id ? { ...r, is_starred: next } : r));
    try {
      await updateSalesRosterMeta(row.user_id, { is_starred: next });
    } catch (err) {
      // Rollback
      setRows(prev => prev.map(r => r.user_id === row.user_id ? { ...r, is_starred: !next } : r));
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Salvataggio stellina fallito', variant: 'destructive' });
    } finally { setStarringId(null); }
  }, [toast]);

  const openNote = useCallback((row, e) => {
    e?.stopPropagation?.();
    setNoteRow(row);
    setNoteDraft(row.note || '');
  }, []);

  const saveNote = useCallback(async () => {
    if (!noteRow) return;
    setSavingNote(true);
    const trimmed = noteDraft.trim();
    // Optimistic
    setRows(prev => prev.map(r => r.user_id === noteRow.user_id ? { ...r, note: trimmed } : r));
    try {
      await updateSalesRosterMeta(noteRow.user_id, { note: trimmed });
      toast({ title: 'Nota salvata', description: trimmed ? `Nota aggiornata su ${noteRow.name}` : `Nota rimossa da ${noteRow.name}` });
      setNoteRow(null);
    } catch (err) {
      // Rollback
      setRows(prev => prev.map(r => r.user_id === noteRow.user_id ? { ...r, note: noteRow.note || '' } : r));
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Salvataggio nota fallito', variant: 'destructive' });
    } finally { setSavingNote(false); }
  }, [noteRow, noteDraft, toast]);

  const sortedRows = useMemo(() => {
    // Filtra le scadenze da mostrare nella tabella esterna: escludi progetti
    // Congelato/Terminato (nel drill-down invece si vede tutto).
    // Il backend le ordina già DESC per days_remaining, quindi [0] = "meno recente".
    const isVisibleForRoster = (e) => {
      const st = (e?.project_status || '').toLowerCase();
      return st !== 'congelato' && st !== 'terminato';
    };
    const enriched = rows.map(r => {
      const visibleExpiring = (r.expiring_products || []).filter(isVisibleForRoster);
      return {
        ...r,
        _roster_expiring: visibleExpiring,
        _shown_expiring_days: visibleExpiring.length > 0 ? visibleExpiring[0].days_remaining : null,
      };
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    enriched.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      // null/undefined sempre in fondo (indipendentemente da asc/desc)
      const aNull = va === null || va === undefined;
      const bNull = vb === null || vb === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
    return enriched;
  }, [rows, sortKey, sortDir]);

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 inline-block ml-1 text-gray-400" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 inline-block ml-1 text-orange-600" />
      : <ArrowDown className="w-3 h-3 inline-block ml-1 text-orange-600" />;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-orange-600" /></div>;
  }

  const cols = [
    { key: 'is_starred', label: <Star className="w-3.5 h-3.5 inline-block" />, align: 'center' },
    { key: 'name', label: 'Venditore', align: 'left' },
    { key: 'months_with_us', label: 'Mesi con noi', align: 'right' },
    { key: 'total_projects_count', label: 'Nº Progetti', align: 'right' },
    { key: 'total_sales', label: 'Vendite totali', align: 'right' },
    { key: 'avg_monthly_sales', label: 'Media/mese', align: 'right' },
    { key: 'last_sale_date', label: 'Ultima vendita', align: 'left' },
    { key: 'sales_commissions', label: 'SF vendite', align: 'right' },
    { key: '__projects', label: 'Stato progetti', align: 'left', notSortable: true },
    { key: '_shown_expiring_days', label: 'Scadenze', align: 'left' },
    { key: 'nps_average', label: 'NPS medio', align: 'right' },
    { key: 'setting_commissions', label: 'SF setting', align: 'right' },
    { key: '__note', label: '', align: 'center', notSortable: true },
    { key: '__actions', label: '', align: 'right', notSortable: true },
  ];

  return (
    <div className="space-y-6" data-testid="sales-roster-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-7 h-7 text-orange-600" />
          Rosa Venditori
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Monitoraggio squadra di venditori
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm text-gray-600">
              <b>{sortedRows.filter(r => !r.is_hidden).length}</b> {sortedRows.filter(r => !r.is_hidden).length === 1 ? 'persona visibile' : 'persone visibili'}
              {hiddenCount > 0 && (
                <span className="text-gray-400 ml-2">· {hiddenCount} nascost{hiddenCount === 1 ? 'a' : 'e'}</span>
              )}
            </div>
            {hiddenCount > 0 && (
              <Button
                variant={showHidden ? 'default' : 'outline'}
                size="sm"
                onClick={() => { const v = !showHidden; setShowHidden(v); load({ showHidden: v }); }}
                data-testid="toggle-show-hidden"
                className="gap-1.5"
              >
                {showHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showHidden ? 'Nascondi persone nascoste' : `Mostra ${hiddenCount} nascost${hiddenCount === 1 ? 'a' : 'e'}`}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {sortedRows.length === 0 ? (
            <div className="border border-dashed rounded-lg p-8 text-center">
              <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Nessuna persona in rosa. Aggiungi utenti con funzione di vendita ai progetti.</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    {cols.map(c => (
                      <TableHead
                        key={c.key}
                        className={`py-3 px-4 font-semibold text-gray-700 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'} whitespace-nowrap`}
                        onClick={() => !c.notSortable && onSort(c.key)}
                        style={{ cursor: c.notSortable ? 'default' : 'pointer', userSelect: 'none' }}
                        data-testid={`col-${c.key}`}
                      >
                        <span className={`inline-flex items-center ${c.align === 'center' ? 'justify-center' : ''}`}>
                          {c.label}
                          {!c.notSortable && <SortIcon col={c.key} />}
                        </span>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map(r => {
                    const rosterExps = r._roster_expiring || [];
                    const shownExp = rosterExps[0] || null;
                    const shownDays = shownExp?.days_remaining ?? null;
                    const isCritical = shownDays !== null && shownDays < 60;
                    const visibleProjects = r.projects.filter(p => !statusMeta(p.status).hidden);
                    return (
                      <TableRow
                        key={r.user_id}
                        className={`cursor-pointer hover:bg-orange-50/60 transition-colors ${r.is_hidden ? 'opacity-50 bg-gray-50/60' : ''}`}
                        onClick={() => setDetailRow(r)}
                        data-testid={`roster-row-${r.user_id}`}
                      >
                        <TableCell className="py-3 px-2 text-center w-10">
                          <button
                            type="button"
                            onClick={(e) => toggleStar(r, e)}
                            disabled={starringId === r.user_id}
                            className={`p-1 rounded-md transition-colors ${r.is_starred ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-300 hover:text-amber-400 hover:bg-amber-50'} disabled:opacity-40`}
                            title={r.is_starred ? 'Rimuovi stellina' : 'Aggiungi stellina'}
                            data-testid={`toggle-star-${r.user_id}`}
                          >
                            {starringId === r.user_id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Star className={`w-4 h-4 ${r.is_starred ? 'fill-amber-500' : ''}`} />}
                          </button>
                        </TableCell>
                        <TableCell className="py-3 px-4 font-medium">
                          <div className="text-gray-900 flex items-center gap-2">
                            {r.name}
                            {r.is_hidden && <Badge variant="outline" className="text-[9px] bg-gray-100 text-gray-500 border-gray-200">Nascosta</Badge>}
                          </div>
                          <div className="text-xs text-gray-500">{r.email}</div>
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right tabular-nums text-gray-700">
                          {r.months_with_us !== null ? r.months_with_us.toLocaleString('it-IT') : '—'}
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right tabular-nums">
                          <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold">
                            {r.total_projects_count}
                          </span>
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right tabular-nums font-semibold text-gray-900">
                          {fmtEURcompact(r.total_sales)}
                          <div className="text-[10px] text-gray-400 font-normal">({r.sales_count})</div>
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right tabular-nums text-gray-700">{fmtEURcompact(r.avg_monthly_sales)}</TableCell>
                        <TableCell className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{fmtDate(r.last_sale_date)}</TableCell>
                        <TableCell className="py-3 px-4 text-right tabular-nums text-green-700 font-medium">{fmtEURcompact(r.sales_commissions)}</TableCell>
                        <TableCell className="py-3 px-4">
                          {visibleProjects.length === 0 ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1 max-w-[220px]">
                              {visibleProjects.slice(0, 3).map(p => {
                                const m = statusMeta(p.status);
                                return (
                                  <Badge key={p.id} variant="outline" className={`text-[10px] font-medium ${m.color}`}>
                                    {m.label}
                                  </Badge>
                                );
                              })}
                              {visibleProjects.length > 3 && (
                                <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-600">
                                  +{visibleProjects.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 px-4">
                          {rosterExps.length === 0 ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm border ${expiryColor(shownDays, shownExp.project_status)}`}>
                              {isCritical && <AlertTriangle className="w-3.5 h-3.5" />}
                              <span>{shownDays}g</span>
                              {rosterExps.length > 1 && (
                                <span className="text-[10px] opacity-70">(+{rosterExps.length - 1})</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className={`py-3 px-4 text-right tabular-nums ${npsColor(r.nps_average)}`}>
                          {r.nps_average !== null && r.nps_average !== undefined ? r.nps_average.toLocaleString('it-IT') : '—'}
                          {r.nps_count > 0 && <div className="text-[10px] text-gray-400 font-normal">({r.nps_count})</div>}
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right tabular-nums text-blue-700 font-medium">{fmtEURcompact(r.setting_commissions)}</TableCell>
                        <TableCell className="py-3 px-2 text-center w-10">
                          <button
                            type="button"
                            onClick={(e) => openNote(r, e)}
                            className={`p-1.5 rounded-md transition-colors relative ${r.note ? 'text-orange-600 hover:bg-orange-100' : 'text-gray-300 hover:text-gray-600 hover:bg-gray-100'}`}
                            title={r.note ? `Nota: ${r.note.slice(0, 80)}${r.note.length > 80 ? '…' : ''}` : 'Aggiungi nota'}
                            data-testid={`open-note-${r.user_id}`}
                          >
                            <StickyNote className={`w-4 h-4 ${r.note ? 'fill-orange-100' : ''}`} />
                            {r.note && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-500 border border-white" />}
                          </button>
                        </TableCell>
                        <TableCell className="py-3 px-4 text-right">
                          <button
                            type="button"
                            onClick={(e) => toggleHidden(r, e)}
                            disabled={togglingId === r.user_id}
                            className={`p-1.5 rounded-md transition-colors ${r.is_hidden ? 'text-orange-600 hover:bg-orange-100' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'} disabled:opacity-40`}
                            title={r.is_hidden ? 'Rimostra nella rosa' : 'Nascondi dalla rosa'}
                            data-testid={`toggle-hide-${r.user_id}`}
                          >
                            {togglingId === r.user_id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : (r.is_hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />)}
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drill-down dettaglio */}
      <Dialog open={!!detailRow} onOpenChange={(open) => { if (!open) setDetailRow(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="roster-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailRow?.name} · Dettaglio</DialogTitle>
          </DialogHeader>
          {detailRow && (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Mesi con noi" value={detailRow.months_with_us !== null ? `${detailRow.months_with_us}` : '—'} />
                <Stat label="Nº Progetti (storico)" value={String(detailRow.total_projects_count)} />
                <Stat label="Vendite totali" value={fmtEUR(detailRow.total_sales)} sub={`${detailRow.sales_count} vendite`} />
                <Stat label="Media mensile" value={fmtEUR(detailRow.avg_monthly_sales)} />
                <Stat label="Ultima vendita" value={fmtDate(detailRow.last_sale_date)} />
                <Stat label="SF da vendite" value={fmtEUR(detailRow.sales_commissions)} color="text-green-700" />
                <Stat label="SF da setting" value={fmtEUR(detailRow.setting_commissions)} color="text-blue-700" />
                <Stat label="NPS medio" value={detailRow.nps_average !== null ? String(detailRow.nps_average) : '—'} sub={`${detailRow.nps_count} feedback`} color={npsColor(detailRow.nps_average)} />
                <Stat label="Primo assignment" value={fmtDate(detailRow.first_assignment_date)} />
              </div>

              <div>
                <h4 className="font-semibold mb-2 text-gray-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Progetti ({detailRow.all_projects?.length ?? detailRow.total_projects_count})
                </h4>
                {(!detailRow.all_projects || detailRow.all_projects.length === 0) ? (
                  <p className="text-xs text-gray-400">Nessun progetto assegnato.</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader className="bg-gray-50">
                        <TableRow>
                          <TableHead className="py-2 px-4">Progetto</TableHead>
                          <TableHead className="py-2 px-4">Stato</TableHead>
                          <TableHead className="py-2 px-4">Scadenza prodotto</TableHead>
                          <TableHead className="py-2 px-4 text-right">Giorni rimasti</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailRow.all_projects.map(p => {
                          const m = statusMeta(p.status);
                          // Scadenze di questo progetto (già ordinate desc dal backend)
                          const projExps = (detailRow.expiring_products || []).filter(e => e.project_id === p.id);
                          const shownExp = projExps[0] || null;
                          const st = (p.status || '').toLowerCase();
                          const isTerminated = st === 'terminato';
                          const isSpecial = st === 'congelato' || isTerminated;
                          return (
                            <TableRow key={p.id} className={p.is_active_assignment ? '' : 'opacity-70'}>
                              <TableCell className="py-2 px-4 font-medium text-gray-800">{p.name}</TableCell>
                              <TableCell className="py-2 px-4">
                                <Badge variant="outline" className={`text-[10px] font-medium ${m.color}`}>
                                  {m.label}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-2 px-4 text-sm">
                                {shownExp ? (
                                  <>
                                    <div className="text-gray-800">{shownExp.product_name}</div>
                                    <div className="text-[11px] text-gray-500">{fmtDate(shownExp.end_date)}{projExps.length > 1 ? ` · +${projExps.length - 1}` : ''}</div>
                                  </>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </TableCell>
                              <TableCell className="py-2 px-4 text-right">
                                {shownExp && !isTerminated ? (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-sm border tabular-nums ${expiryColor(shownExp.days_remaining, p.status)}`}>
                                    {shownExp.days_remaining < 60 && !isSpecial && <AlertTriangle className="w-3 h-3" />}
                                    {shownExp.days_remaining}g
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog nota */}
      <Dialog open={!!noteRow} onOpenChange={(open) => { if (!open && !savingNote) { setNoteRow(null); setNoteDraft(''); } }}>
        <DialogContent className="max-w-md" data-testid="roster-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-4 h-4 text-orange-600" />
              Nota su {noteRow?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Aggiungi una nota interna (es. in ferie, in uscita, punti di forza…)"
              rows={5}
              disabled={savingNote}
              data-testid="roster-note-textarea"
              autoFocus
            />
            <div className="flex justify-between items-center gap-2">
              <div className="text-[11px] text-gray-400">{noteDraft.length} caratteri</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setNoteRow(null); setNoteDraft(''); }} disabled={savingNote} data-testid="roster-note-cancel">
                  Annulla
                </Button>
                <Button size="sm" onClick={saveNote} disabled={savingNote} data-testid="roster-note-save">
                  {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div className="border rounded-lg p-3 bg-gray-50">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className={`text-base font-semibold ${color || 'text-gray-900'}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}
